/**
 * useNavigationStack - Hook para gestionar navegación con historial
 *
 * FASE 3: Implementa stack-based navigation similar a apps móviles
 *
 * Features:
 * - Push: agregar nueva vista al historial
 * - Pop: volver a vista anterior
 * - canGoBack: saber si hay historial
 * - Clear: limpiar todo el historial
 * - Persistencia en sessionStorage (se limpia al cerrar tab)
 *
 * Ejemplo de uso:
 * ```tsx
 * const { push, pop, canGoBack, currentState } = useNavigationStack();
 *
 * // Navegar a un PDF
 * push({ view: 'viewer', data: pdfViewerData });
 *
 * // Volver atrás
 * if (canGoBack) {
 *   const previousState = pop();
 *   setMainView(previousState.view);
 * }
 * ```
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import logger from '../utils/simpleLogger';

// MainView includes all 5 main navigation options from the menu:
// - chat, viewer, graph-pro: Full-screen views with navigation history
// - drive, studios: Full-screen panels that behave as main views
export type MainView = 'chat' | 'viewer' | 'graph-pro' | 'drive' | 'studios';

export interface NavigationState {
  view: MainView;
  data?: any; // ViewerData para viewer, null para otros
  timestamp: number;
  label?: string; // Descripción opcional para debugging
}

interface NavigationStack {
  stack: NavigationState[];
  currentIndex: number;
}

const STORAGE_KEY = 'navigation-stack';
const MAX_STACK_SIZE = 50; // Evitar memoria infinita

export function useNavigationStack(initialView: MainView = 'chat') {
  // Estado del stack
  const [navStack, setNavStack] = useState<NavigationStack>(() => {
    // Intentar restaurar desde sessionStorage
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.stack && Array.isArray(parsed.stack)) {
          return parsed;
        }
      }
    } catch (error) {
      logger.warn('⏳ No se pudo restaurar navigation stack', error);
    }

    // Stack inicial
    return {
      stack: [
        {
          view: initialView,
          timestamp: Date.now(),
          label: 'Inicio',
        },
      ],
      currentIndex: 0,
    };
  });

  // Referencia para evitar loops infinitos en useEffect
  const isInitialMount = useRef(true);

  // Persistir en sessionStorage cuando cambie el stack
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(navStack));
    } catch (error) {
      logger.warn('⏳ No se pudo guardar navigation stack', error);
    }
  }, [navStack]);

  /**
   * Estado actual de navegación
   */
  const currentState = navStack.stack[navStack.currentIndex];

  /**
   * ¿Puedo ir hacia atrás?
   */
  const canGoBack = navStack.currentIndex > 0;

  /**
   * ¿Puedo ir hacia adelante?
   */
  const canGoForward = navStack.currentIndex < navStack.stack.length - 1;

  /**
   * Agregar nueva vista al historial (PUSH)
   *
   * Comportamiento:
   * - Si estás en medio del stack, elimina todo lo que viene después
   * - Agrega la nueva vista al final
   * - Limita el stack a MAX_STACK_SIZE
   */
  const push = useCallback((state: Omit<NavigationState, 'timestamp'>) => {
    logger.info('🧭 [Nav] push() called', { state });
    setNavStack((prev) => {
      // Eliminar todo después del índice actual (si navegaste hacia atrás)
      const newStack = prev.stack.slice(0, prev.currentIndex + 1);

      // Agregar nueva vista
      newStack.push({
        ...state,
        timestamp: Date.now(),
      });

      // Limitar tamaño del stack
      const trimmedStack = newStack.slice(-MAX_STACK_SIZE);

      return {
        stack: trimmedStack,
        currentIndex: trimmedStack.length - 1,
      };
    });
  }, []);

  /**
   * Volver a la vista anterior (POP)
   *
   * @returns La vista anterior, o null si no hay
   */
  const pop = useCallback((): NavigationState | null => {
    if (!canGoBack) {
      logger.warn('🧭 [Nav] pop() called but no history');
      return null;
    }

    let previousState: NavigationState | null = null;

    setNavStack((prev) => {
      const newIndex = Math.max(0, prev.currentIndex - 1);
      previousState = prev.stack[newIndex];

      return {
        ...prev,
        currentIndex: newIndex,
      };
    });

    logger.info('🧭 [Nav] pop() resulted in', { previousState });
    return previousState;
  }, [canGoBack]);

  /**
   * Ir hacia adelante en el historial
   *
   * @returns La siguiente vista, o null si no hay
   */
  const forward = useCallback((): NavigationState | null => {
    if (!canGoForward) {
      logger.warn('🧭 [Nav] forward() called but no future state');
      return null;
    }

    let nextState: NavigationState | null = null;

    setNavStack((prev) => {
      const newIndex = Math.min(prev.stack.length - 1, prev.currentIndex + 1);
      nextState = prev.stack[newIndex];

      return {
        ...prev,
        currentIndex: newIndex,
      };
    });

    logger.info('🧭 [Nav] forward() resulted in', { nextState });
    return nextState;
  }, [canGoForward]);

  /**
   * Reemplazar el estado actual sin agregar al historial
   *
   * Útil para actualizar datos de la vista actual sin crear nueva entrada
   */
  const replace = useCallback((state: Omit<NavigationState, 'timestamp'>) => {
    logger.info('🧭 [Nav] replace() called', { state });
    setNavStack((prev) => {
      const newStack = [...prev.stack];
      newStack[prev.currentIndex] = {
        ...state,
        timestamp: Date.now(),
      };

      return {
        ...prev,
        stack: newStack,
      };
    });
  }, []);

  /**
   * Limpiar todo el historial y volver al inicio
   */
  const clear = useCallback(
    (initialState?: Omit<NavigationState, 'timestamp'>) => {
      logger.info('🧭 [Nav] clear() called', { initialState });
      setNavStack({
        stack: [
          {
            view: initialState?.view || initialView,
            data: initialState?.data,
            timestamp: Date.now(),
            label: initialState?.label || 'Inicio (limpiado)',
          },
        ],
        currentIndex: 0,
      });
    },
    [initialView]
  );

  /**
   * Obtener historial completo (para debugging)
   */
  const getHistory = useCallback(() => {
    logger.info('🧭 [Nav] getHistory() called');
    return {
      stack: navStack.stack,
      currentIndex: navStack.currentIndex,
      current: currentState,
    };
  }, [navStack, currentState]);

  /**
   * Ir directamente a un índice específico del historial
   */
  const goToIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= navStack.stack.length) {
        logger.warn('🧭 [Nav] goToIndex() out of range', { index });
        return null;
      }

      let targetState: NavigationState | null = null;

      setNavStack((prev) => {
        targetState = prev.stack[index];
        return {
          ...prev,
          currentIndex: index,
        };
      });

      logger.info('🧭 [Nav] goToIndex() result', { targetState });
      return targetState;
    },
    [navStack.stack.length]
  );

  return {
    // Estado actual
    currentState,

    // Navegación
    push,
    pop,
    forward,
    replace,

    // Estado de navegación
    canGoBack,
    canGoForward,

    // Utilidades
    clear,
    getHistory,
    goToIndex,

    // Datos del stack (para debugging)
    stackSize: navStack.stack.length,
    currentIndex: navStack.currentIndex,
  };
}

export default useNavigationStack;
